/**
 * Runtime (App mode) API client.
 *
 * Communicates with /api/v1/app/{connectionId}/{objectName}/* endpoints.
 */
import { ApiService } from '@/utils/api'
import type { ConfiguredLanguage, MyPermissions } from '@/app/permissions/permissionHelpers'
import type { PrintCommand } from '@/runtime/print/types'
import type { FilledSpreadsheetModel } from '@/runtime/print/spreadsheet/types'

export type { PrintCommand }

export interface PrintExecuteResponse {
  model: FilledSpreadsheetModel
  title: string
  locale?: string | null
  languages?: { code: string; name: string; isDefault: boolean }[]
}

export interface DeleteRecordResponse {
  /** IDs of records that were cascaded (deleted as descendants) */
  cascaded_ids?: string[]
  /** Count of predefined records that were skipped during cascade */
  skipped_predefined?: number
}

const BASE = '/v1/app'

/** Pause/resume dialog spec returned when LavkaLang calls show_*_box (unified dialogs epic). */
export interface InteractionDialogSpec {
  text: string
  mode: string
  kind: string
}

/** Response shape when the server needs a user answer before completing the operation. */
export interface InteractionRequiredPayload {
  interaction_required: true
  dialog: InteractionDialogSpec
  answered_count: number
}

export type RuntimeMutationResult = Record<string, any> & Partial<InteractionRequiredPayload>

export interface FieldEventResponse {
  data: Record<string, any> | null
  messages: string[]
  error: string | null
  interaction_required?: boolean
  dialog?: InteractionDialogSpec
  answered_count?: number
}

export interface FieldEventBody {
  field: string
  tabular_part?: string | null
  row_index?: number | null
  data: Record<string, any>
  record_id?: string | null
  interaction_answers?: string[]
}

export interface ListMeta {
  page: number
  limit: number
  total: number
}

export interface ListResponse<T = Record<string, any>> {
  data: T[]
  meta: ListMeta
}

/** One group-level summary row from the `group_by` list mode. */
export interface GroupSummaryItem {
  values: Record<string, unknown>
  count: number
  sums: Record<string, number>
}

export interface GroupSummaryResponse {
  groups: GroupSummaryItem[]
  meta?: { total_groups: number }
}

/** Response from the `aggregate` list mode — one summary value per `field:op` spec.
 * `null` for sum/avg/min/max over an empty (fully filtered-out) set — `count` stays `0`. */
export interface AggregateResponse {
  data: Record<string, number | string | null>
  meta: Record<string, unknown>
  errors: unknown[]
}

export interface ListGroupParams {
  /** Grouping fields for this level (usually a single field per lazy level). */
  group_by: string[]
  /** Whitelisted equality filters (parent-path values + schema filters), URL-encoded JSON. */
  filters?: Record<string, unknown>
  search?: string
  search_fields?: string[]
  include_deleted?: boolean
  /** Active i18next locale — resolves translatable reference/list display server-side. */
  locale?: string
}

export interface ListAggregateParams {
  /** `field:op` specs (op in sum|avg|min|max|count), e.g. `["amount:sum", "amount:avg"]`. */
  aggregate: string[]
  /** Whitelisted equality filters (parent-path values + schema filters), URL-encoded JSON. */
  filters?: Record<string, unknown>
  search?: string
  search_fields?: string[]
  include_deleted?: boolean
  filter_date_field?: string
  date_from?: string
  date_to?: string
  /** Active i18next locale — resolves translatable reference/list display server-side. */
  locale?: string
}

export interface ListParams {
  page?: number
  limit?: number
  search?: string
  sort_field?: string
  sort_dir?: 'asc' | 'desc'
  include_deleted?: boolean
  filter_date_field?: string
  date_from?: string
  date_to?: string
  /** Whitelisted equality filters (field → value), sent as URL-encoded JSON. */
  filters?: Record<string, unknown>
  /** Attribute names the server should OR-search `search` across (CSV). */
  search_fields?: string[]
  /** Active i18next locale — resolves translatable reference/list display server-side. */
  locale?: string
}

export function buildQuery(connectionId: string, objectName: string, params?: ListParams): string {
  const base = `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}`
  if (!params) return base
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.search) qs.set('search', params.search)
  if (params.sort_field) qs.set('sort_field', params.sort_field)
  if (params.sort_dir) qs.set('sort_dir', params.sort_dir)
  if (params.include_deleted) qs.set('include_deleted', 'true')
  if (params.filter_date_field) qs.set('filter_date_field', params.filter_date_field)
  if (params.date_from) qs.set('date_from', params.date_from)
  if (params.date_to) qs.set('date_to', params.date_to)
  if (params.filters && Object.keys(params.filters).length) {
    qs.set('filters', JSON.stringify(params.filters))
  }
  // `[]` must serialize as `search_fields=` (BE: no predicate), not be omitted (BE: legacy).
  if (params.search_fields !== undefined) qs.set('search_fields', params.search_fields.join(','))
  if (params.locale) qs.set('locale', params.locale)
  const str = qs.toString()
  return str ? `${base}/list?${str}` : `${base}/list`
}

/**
 * Builds the `group_by` list URL for group-summary mode. Shares the `/list`
 * endpoint with `buildQuery`; the presence of `group_by` flips the server into
 * returning group summaries (values/count/sums) instead of rows.
 */
export function buildGroupQuery(connectionId: string, objectName: string, params: ListGroupParams): string {
  const base = `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/list`
  const qs = new URLSearchParams()
  qs.set('group_by', params.group_by.join(','))
  if (params.filters && Object.keys(params.filters).length) {
    qs.set('filters', JSON.stringify(params.filters))
  }
  if (params.search) qs.set('search', params.search)
  // `[]` must serialize as `search_fields=` (BE: no predicate), not be omitted (BE: legacy).
  if (params.search_fields !== undefined) qs.set('search_fields', params.search_fields.join(','))
  if (params.include_deleted) qs.set('include_deleted', 'true')
  if (params.locale) qs.set('locale', params.locale)
  return `${base}?${qs.toString()}`
}

/**
 * Builds the `aggregate` list URL for aggregate-summary mode (list-footer totals).
 * Shares the `/list` endpoint and the same filter/search/date param logic as
 * `buildQuery`; the presence of `aggregate` flips the server into returning a
 * single summary row (`{"amount:sum": 1234.5, ...}`) instead of rows.
 */
export function buildAggregateQuery(
  connectionId: string,
  objectName: string,
  params: ListAggregateParams,
): string {
  const base = `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/list`
  const qs = new URLSearchParams()
  qs.set('aggregate', params.aggregate.join(','))
  if (params.search) qs.set('search', params.search)
  if (params.include_deleted) qs.set('include_deleted', 'true')
  if (params.filter_date_field) qs.set('filter_date_field', params.filter_date_field)
  if (params.date_from) qs.set('date_from', params.date_from)
  if (params.date_to) qs.set('date_to', params.date_to)
  if (params.filters && Object.keys(params.filters).length) {
    qs.set('filters', JSON.stringify(params.filters))
  }
  // `[]` must serialize as `search_fields=` (BE: no predicate), not be omitted (BE: legacy).
  if (params.search_fields !== undefined) qs.set('search_fields', params.search_fields.join(','))
  if (params.locale) qs.set('locale', params.locale)
  return `${base}?${qs.toString()}`
}

export const runtimeApi = {
  /** List records with pagination, search, sorting */
  list: (connectionId: string, objectName: string, params?: ListParams) =>
    ApiService.get<ListResponse>(buildQuery(connectionId, objectName, params)),

  /** Group-summary mode: one level of the lazy group tree (values/count/sums). */
  listGroups: (connectionId: string, objectName: string, params: ListGroupParams) =>
    ApiService.get<GroupSummaryResponse>(buildGroupQuery(connectionId, objectName, params)),

  /** Aggregate-summary mode: sum/avg/min/max/count over the filtered set (list-footer totals). */
  aggregate: (connectionId: string, objectName: string, params: ListAggregateParams) =>
    ApiService.get<AggregateResponse>(buildAggregateQuery(connectionId, objectName, params)),

  /** Get a single record by ID (includes tabular parts) */
  get: (connectionId: string, objectName: string, recordId: string, locale?: string) =>
    ApiService.get<Record<string, any>>(
      `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}` +
        (locale ? `?locale=${encodeURIComponent(locale)}` : ''),
    ),

  /** Create a new record */
  create: (connectionId: string, objectName: string, data: Record<string, any>) =>
    ApiService.post<RuntimeMutationResult>(
      `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}`,
      data,
    ),

  /** Update an existing record */
  update: (connectionId: string, objectName: string, recordId: string, data: Record<string, any>) =>
    ApiService.put<RuntimeMutationResult>(
      `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}`,
      data,
    ),

  /** Run an attribute's on_change handler against unsaved form state (Task 3, field-events epic). */
  runFieldEvent: (
    connectionId: string,
    objectName: string,
    body: FieldEventBody,
  ): Promise<FieldEventResponse> =>
    ApiService.post<FieldEventResponse>(
      `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/field-event`,
      body,
    ),

  /** Visible print commands (layout-seeded + optional print_commands handler). */
  printCommands: (
    connectionId: string,
    objectName: string,
    recordId?: string | null,
  ): Promise<PrintCommand[]> => {
    const base = `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/print-commands`
    const qs =
      recordId != null && recordId !== ''
        ? `?record_id=${encodeURIComponent(recordId)}`
        : ''
    return ApiService.get<PrintCommand[]>(`${base}${qs}`)
  },

  /** Execute a print command → filled spreadsheet model + title. */
  print: (
    connectionId: string,
    objectName: string,
    body: { command_id: string; record_id: string; locale?: string | null },
  ): Promise<PrintExecuteResponse> =>
    ApiService.post<PrintExecuteResponse>(
      `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/print`,
      body,
    ),

  /** Soft-delete a record */
  delete: (connectionId: string, objectName: string, recordId: string) =>
    ApiService.delete<DeleteRecordResponse>(
      `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}`,
    ),

  /** Restore a soft-deleted record */
  undelete: (connectionId: string, objectName: string, recordId: string) =>
    ApiService.post<any>(
      `${BASE}/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}/undelete`,
      {},
    ),

  /** The current user's effective rights on this connection (Task 5, ERP Users & Access Rights epic). */
  myPermissions: (connectionId: string) =>
    ApiService.get<MyPermissions>(`${BASE}/${encodeURIComponent(connectionId)}/my-permissions`),

  /** Persist the member's App locale on this connection. */
  setMyLocale: (connectionId: string, code: string) =>
    ApiService.put<{ languages: ConfiguredLanguage[]; locale: string | null }>(
      `${BASE}/${encodeURIComponent(connectionId)}/my-locale`,
      { code },
    ),

  /** Mint a fresh one-time temp password for a member's linked platform account. */
  resetUserPassword: (connectionId: string, recordId: string) =>
    ApiService.post<{ temp_password: string }>(
      `${BASE}/${encodeURIComponent(connectionId)}/_users/${encodeURIComponent(recordId)}/reset-password`,
      {},
    ),
}
